/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * FeedLayout
 * ----------
 * Shell for the athlete surface under /feed: left navigation, the routed page,
 * and on phones a top bar and bottom tabs. Renders on the fixed FEED_TOKENS
 * palette (the portal's agni orange) — like the organizer surface, it does not
 * follow the theme switcher.
 *
 * The home page supplies two grid columns (feed + right rail); sub-pages
 * (profile, notifications, analytics) supply one, so the grid is told which shape to use.
 */

import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import FEED_TOKENS from '../../theme/feedTokens';
import { FeedSidebar, FeedTopBar, FeedBottomNav } from '../../components/feed/FeedSidebar';
import { useFeedUser, useAthleteSummary } from './useFeedData';
import ProfileDetailsGate from './profile/ProfileDetailsGate';
import {
  FEED_PROFILE_PATH, FEED_NOTIFICATIONS_PATH, FEED_ANALYTICS_PATH,
  FEED_EVENTS_PATH, FEED_PASSES_PATH, FEED_CERTIFICATES_PATH,
  FEED_PAYMENTS_PATH,
} from './feedRoutes';
import '../../styles/feed.css';

export default function FeedLayout() {
  const user = useFeedUser();
  // Same cache entry the page uses — the badge costs no extra request.
  const { data: summary } = useAthleteSummary(user.userId);
  const { pathname } = useLocation();
  // const isSub = pathname.startsWith(FEED_PROFILE_PATH) || pathname.startsWith(FEED_NOTIFICATIONS_PATH);
  // Events and Passes are single-column too: they fill the same middle slot,
  // and there is no right rail to make room for.
  const isSub = [
    FEED_PROFILE_PATH, FEED_NOTIFICATIONS_PATH, FEED_ANALYTICS_PATH,
    FEED_EVENTS_PATH, FEED_PASSES_PATH, FEED_CERTIFICATES_PATH,
    FEED_PAYMENTS_PATH,
  ].some((p) => pathname.startsWith(p));
  // Registered events, excluding draft/completed/cancelled events and
  // cancelled/blocked registrations (athlete_registration_summary).
  const count = summary?.registrations?.total || 0;

  return (
    <div className="fd-shell" style={FEED_TOKENS}>
      <div className={`fd-grid${isSub ? ' fd-wide' : ''}`}>
        <FeedSidebar user={user} registrationCount={count} />
        <FeedTopBar user={user} />
        <Outlet />
      </div>
      <FeedBottomNav registrationCount={count} />
      {/* First sign-in (or any profile missing gender/height/weight): ask before the feed. */}
      <ProfileDetailsGate userId={user.userId} />
    </div>
  );
}
